import React, { useState, useRef, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import instance from "./axiosConfig";
import axios from "axios";
import {
  Play,
  Pause,
  CheckCircle2,
  Video,
  Music,
  ArrowUp,
  ArrowDown,
  AlignCenter,
  Plus,
  X,
  Download,
  Zap,
  FileVideo,
  Clock,
  Share2,
  MessageSquare,
  ArrowRight,
  Eye,
  Loader2,
  Settings,
  Gift,
  Sparkles,
  ChevronRight,
  UploadCloud,
  Check,
  RefreshCw,
  Search,
  Info,
  Filter,
  Signal,
  Wifi,
  Battery,
  Heart,
  MessageCircle,
  Bookmark,
  Share,
} from "lucide-react";
import UppyUploader from "./UppyUploader";
import EnhancedHeader from "./EnhancedHeader";

const VideoCreator = () => {
  // Main flow state

  const [pollingInterval, setPollingInterval] = useState(null);
  const [userProcessedVideos, setUserProcessedVideos] = useState([]);
  const [showLibrary, setShowLibrary] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [processing, setProcessing] = useState(false);
  const [showFinalVideo, setShowFinalVideo] = useState(false);

  const [projectId, setProjectId] = useState(null);
  const [processingStep, setProcessingStep] = useState("");
  const [processingProgress, setProcessingProgress] = useState(0);
  const [TaskId, setTaskId] = useState(null);

  // Template selection state (Step 1)
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(true);
  const [previewingTemplate, setPreviewingTemplate] = useState(null);

  // Hook caption state (Step 2)
  const [hookCaption, setHookCaption] = useState("");
  const [captionPosition, setCaptionPosition] = useState("top");

  // Demo video state (Step 3)
  const [includeDemoVideo, setIncludeDemoVideo] = useState(false);
  // const [includeDemoVideo, setIncludeDemoVideo] = useState(true);
  const [demoVideo, setDemoVideo] = useState(null);
  const [uploadingDemo, setUploadingDemo] = useState(false);
  const [timelineCaptions, setTimelineCaptions] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Audio selection
  const [backgroundMusic, setBackgroundMusic] = useState(null);
  const [musicTracks, setMusicTracks] = useState([]);
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);

  // Video playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Final output
  const [finalVideoUrl, setFinalVideoUrl] = useState(null);
  const [showCustomTemplateModal, setShowCustomTemplateModal] = useState(false);
  const [userDemoVideos, setUserDemoVideos] = useState([]);
  const [videoUrl, setVideoUrl] = useState(null);
  // const [captionBg, setCaptionBg] = useState("black");
  const [captionBg, setCaptionBg] = useState("transparent");

  const [selectedCategory, setSelectedCategory] = useState("people_expression");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDemoFaq, setShowDemoFaq] = useState(false);

  // User data
  const [userCredits, setUserCredits] = useState({
    video_credits: 0,
    hasPreviewAccess: false,
  });

  // Refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const progressBarRef = useRef(null);
  const [demoVideoLoading, setDemoVideoLoading] = useState(false);
  const [selectedFont, setSelectedFont] = useState("montserrat");
  const [fontSize, setFontSize] = useState("small");

  const categories = [
    // { value: "", label: "All Categories" },
    { value: "people_expression", label: "People Expression" },
    { value: "avatar_cartoon", label: "Avatar & Cartoon Style" },
    { value: "cinematic", label: "Cinematic Snippets" },
    { value: "icon_text", label: "Icon / Illustration + Text" },

    { value: "meme_remix", label: "Meme Remix" },
    { value: "minimalist_text", label: "Minimalist Text & Background" },
  ];

  const fetchUserProcessedVideos = async () => {
    try {
      const response = await instance.get("api/processed-videos/");
      setUserProcessedVideos(response.data);
    } catch (error) {
      console.error("Failed to fetch processed videos:", error);
    }
  };

  // 4. Add useEffect to load processed videos when component mounts
  useEffect(() => {
    fetchUserProcessedVideos();
  }, []);

  useEffect(() => {
    const fetchUserCredits = async () => {
      try {
        const response = await instance.get("get-user-credits/");

        // Store credit information
        setUserCredits({
          video_credits: response.data.video_credits,
          hasPreviewAccess: !response.data.accessed_video_free_preview,
          plan_name: response.data.plan_name,
          subscription_active: response.data.subscription_active,
        });

        console.log("📊 User credits loaded:", response.data);
      } catch (error) {
        console.error("❌ Failed to fetch user credits:", error);
        showNotification("Error", "Failed to load your credit information. Please refresh the page.", "error");
      }
    };

    fetchUserCredits();
  }, []);

  const showNotification = (title, message, type = "info") => {
    // Create notification container if it doesn't exist
    let container = document.getElementById("notification-container");
    if (!container) {
      container = document.createElement("div");
      container.id = "notification-container";
      container.className = "fixed top-4 right-4 z-50 flex flex-col gap-2";
      document.body.appendChild(container);
    }

    // Create notification element
    const notification = document.createElement("div");
    notification.className = `max-w-md p-4 mb-4 rounded-lg shadow-lg flex items-start gap-3 transform translate-x-0 transition-all duration-500 ${
      type === "error"
        ? "bg-red-50 border-l-4 border-red-500"
        : type === "success"
        ? "bg-green-50 border-l-4 border-green-500"
        : type === "warning"
        ? "bg-amber-50 border-l-4 border-amber-500"
        : "bg-blue-50 border-l-4 border-blue-500"
    }`;

    const iconColor =
      type === "error" ? "text-red-500" : type === "success" ? "text-green-500" : type === "warning" ? "text-amber-500" : "text-blue-500";

    // Add content
    notification.innerHTML = `
      <div className="flex-shrink-0">
        ${
          type === "error"
            ? `<svg class="w-5 h-5 ${iconColor}" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>`
            : type === "success"
            ? `<svg class="w-5 h-5 ${iconColor}" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>`
            : type === "warning"
            ? `<svg class="w-5 h-5 ${iconColor}" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>`
            : `<svg class="w-5 h-5 ${iconColor}" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>`
        }
      </div>
      <div class="flex-1">
        <h3 class="font-medium text-gray-900">${title}</h3>
        <p class="text-sm text-gray-600">${message}</p>
      </div>
      <button class="flex-shrink-0 text-gray-400 hover:text-gray-600" onclick="this.parentElement.remove()">
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    `;

    // Add to container
    container.appendChild(notification);

    // Auto remove after 6 seconds
    setTimeout(() => {
      notification.style.opacity = "0";
      notification.style.transform = "translateX(100%)";
      setTimeout(() => {
        if (notification.parentElement) {
          notification.remove();
        }
      }, 300);
    }, 6000);
  };

  //  useEffect to fetch the user's videos:

  const fetchDemoVideos = async () => {
    try {
      console.log("Fetching user demo videos...");
      const response = await instance.get("api/user-demo-videos/");
      console.log("User demo videos response:", response.data);
      setUserDemoVideos(response.data || []);
    } catch (error) {
      console.error("Failed to fetch user demo videos:", error);
      setUserDemoVideos([]);
    }
  };

  // useEffect(() => {
  //   if (currentStep === 3) {
  //     fetchDemoVideos();
  //   }
  // }, [currentStep]);

  // Modified hook to fetch demo videos ON MOUNT
  useEffect(() => {
    // Remove the 'if (currentStep === 3)' condition
    console.log("🔧 Fetching demo videos on component mount..."); // Log added for clarity
    fetchDemoVideos(); // Fetch unconditionally when the component mounts
  }, []); // <-- Change dependency array to EMPTY [] to run only once on mount

  // Fetch user credits from API
  useEffect(() => {
    const fetchUserCredits = async () => {
      try {
        const response = await instance.get("get-user-credits/");
        setUserCredits({
          video_credits: response.data.video_credits,
          hasPreviewAccess: !response.data.accessed_video_free_preview,
        });
      } catch (error) {
        console.error("Failed to fetch user credits:", error);
      }
    };

    fetchUserCredits();
  }, []);

  // Add this useEffect for the final video check
  useEffect(() => {
    // Function to do one final check for the processed video
    const finalVideoCheck = async () => {
      if (!projectId || !processing || finalVideoUrl) return;

      console.log("Performing final video check for project:", projectId);

      try {
        const response = await instance.get(`api/video-status/${projectId}/`);

        if (response.data.is_processed && response.data.processed_video_url) {
          const videoUrl = response.data.processed_video_url;

          if (videoUrl && videoUrl.trim() !== "") {
            console.log("Final check found video URL:", videoUrl);
            setFinalVideoUrl(videoUrl);

            // Show final video
            setTimeout(() => {
              setShowFinalVideo(true);
              setProcessing(false);

              // Also refresh the user's library
              fetchUserProcessedVideos();
            }, 1000);
          }
        }
      } catch (error) {
        console.error("Error in final video check:", error);
      }
    };

    // Set up a special timeout check for when processing has been happening for a long time
    // This helps catch cases where the backend is done but the frontend missed the notification
    let finalCheckTimeout;

    if (processing && projectId) {
      finalCheckTimeout = setTimeout(finalVideoCheck, 60000); // Extra check after 1 minute
    }

    return () => {
      if (finalCheckTimeout) clearTimeout(finalCheckTimeout);
    };
  }, [processing, projectId, finalVideoUrl]);

  useEffect(() => {
    const fetchTemplates = async () => {
      setTemplateLoading(true);
      try {
        const response = await instance.get("api/hook-videos/", {
          params: {
            category: selectedCategory,
            page: currentPage,
            page_size: 100,
          },
        });
        setTemplates(response.data.results);
        setTotalPages(response.data.total_pages);
      } catch (error) {
        console.error("Failed to fetch templates:", error);
      } finally {
        setTemplateLoading(false);
      }
    };

    fetchTemplates();
  }, [selectedCategory, currentPage]);

  // Move the useEffect to component level
  // useEffect(() => {
  //   if (!hookCaption) {
  //     setHookCaption("I think i might have found the ... app everyone is trying to hide");
  //   }
  //   if (!selectedTemplate && templates.length > 0) {
  //     const defaultTemplate = templates[0];
  //     setSelectedTemplate(defaultTemplate);
  //     if (videoRef.current) {
  //       videoRef.current.src = defaultTemplate.s3_url;
  //       videoRef.current.load();
  //       videoRef.current
  //         .play()
  //         .then(() => setIsPlaying(true))
  //         .catch((error) => console.error("Video playback failed:", error));
  //     }
  //   }
  // }, [templates, hookCaption, selectedTemplate]);

  // useEffect(() => {
  //   if (!hookCaption) {
  //     setHookCaption("I think i might have found the ... app everyone is trying to hide");
  //   }

  //   if (!selectedTemplate && templates.length > 0) {
  //     // Get last 10 (or fewer) templates
  //     const recentTemplates = templates.slice(-10);

  //     // Pick a random one
  //     const randomIndex = Math.floor(Math.random() * recentTemplates.length);
  //     const defaultTemplate = recentTemplates[randomIndex];

  //     setSelectedTemplate(defaultTemplate);

  //     if (videoRef.current) {
  //       videoRef.current.src = defaultTemplate.s3_url;
  //       videoRef.current.load();
  //       videoRef.current
  //         .play()
  //         .then(() => setIsPlaying(true))
  //         .catch((error) => console.error("Video playback failed:", error));
  //     }
  //   }
  // }, [templates, hookCaption, selectedTemplate]);

  // --- START EXACT FIX for useEffect ---
  useEffect(() => {
    // Set default caption if needed (no change here)
    if (!hookCaption) {
      setHookCaption("I think i might have found the ... app everyone is trying to hide");
    }

    // Logic to select the *initial* template when templates load
    if (!selectedTemplate && templates.length > 0) {
      // Get last 10 (or fewer) templates
      const recentTemplates = templates.slice(-10);

      // Pick a random one
      const randomIndex = Math.floor(Math.random() * recentTemplates.length);
      const defaultTemplate = recentTemplates[randomIndex];

      // Set the selected template state
      setSelectedTemplate(defaultTemplate);

      // If the video element exists, set its source and load it
      if (videoRef.current) {
        console.log("Setting initial template source:", defaultTemplate.s3_url);
        videoRef.current.src = defaultTemplate.s3_url;
        videoRef.current.load(); // Tell the browser to load the new source

        // Ensure the playback state is initially set to paused
        // as we are no longer auto-playing here.
        setIsPlaying(false);
      }
    }
    // Dependencies: This effect primarily runs when templates load to set the initial default.
    // It might run again if selectedTemplate is somehow reset to null later.
  }, [templates, hookCaption, selectedTemplate]); // Keep dependencies for now
  // --- END EXACT FIX for useEffect ---

  // Fetch music tracks from API
  useEffect(() => {
    const fetchMusicTracks = async () => {
      try {
        const response = await instance.get("api/background-music/");
        setMusicTracks(response.data);
        // Select first track by default if available
        if (response.data.length > 0) {
          setBackgroundMusic(response.data[0]);
        }
      } catch (error) {
        console.error("Failed to fetch music tracks:", error);
      }
    };

    fetchMusicTracks();
  }, []);

  // const handleTemplatePreview = (template) => {
  //   setPreviewingTemplate(template);
  //   setSelectedTemplate(template);

  //   // Pre-populate hook caption based on template metadata if available
  //   if (template.metadata && template.metadata.suggested_caption) {
  //     setHookCaption(template.metadata.suggested_caption);
  //   } else {
  //     // setHookCaption("✨ Make Your Product Stand Out!");
  //     setHookCaption("I think i might have found the ... app everyone is trying to hide");
  //   }

  //   // Set up video preview
  //   if (videoRef.current) {
  //     videoRef.current.src = template.s3_url;
  //     videoRef.current.load();

  //     // Play the video after it's loaded
  //     videoRef.current.onloadeddata = () => {
  //       videoRef.current
  //         .play()
  //         .then(() => setIsPlaying(true))
  //         .catch((error) => console.error("Video playback failed:", error));
  //     };
  //   }
  // };

  // const handleTemplatePreview = (template) => {
  //   setPreviewingTemplate(template);
  //   setSelectedTemplate(template);

  //   // --- Start Fix ---
  //   const initialDefaultCaption = "I think i might have found the ... app everyone is trying to hide";

  //   // Only update the caption if the user hasn't typed anything custom yet
  //   if (!hookCaption || hookCaption === initialDefaultCaption) {
  //     // Pre-populate hook caption based on template metadata if available
  //     if (template.metadata && template.metadata.suggested_caption) {
  //       setHookCaption(template.metadata.suggested_caption);
  //     } else {
  //       // If template has no suggestion, and caption was default/empty, reset to default
  //       setHookCaption(initialDefaultCaption);
  //     }
  //   }
  //   // ELSE: If hookCaption already has user text, leave it unchanged.
  //   // --- End Fix ---

  //   // Set up video preview
  //   if (videoRef.current) {
  //     videoRef.current.src = template.s3_url;
  //     videoRef.current.load();

  //     // Play the video after it's loaded
  //     videoRef.current.onloadeddata = () => {
  //       videoRef.current
  //         .play()
  //         .then(() => setIsPlaying(true))
  //         .catch((error) => console.error("Video playback failed:", error));
  //     };
  //   }
  // };
  // --- START: Minimal Fix 2: Correct handleTemplatePreview ---
  const handleTemplatePreview = (template) => {
    setPreviewingTemplate(template);
    setSelectedTemplate(template);

    const initialDefaultCaption = "I think i might have found the ... app everyone is trying to hide";
    if (!hookCaption || hookCaption === initialDefaultCaption) {
      if (template.metadata && template.metadata.suggested_caption) {
        setHookCaption(template.metadata.suggested_caption);
      } else {
        setHookCaption(initialDefaultCaption);
      }
    }

    if (videoRef.current) {
      console.log("Template selected, setting src and loading:", template.s3_url);
      videoRef.current.src = template.s3_url;
      videoRef.current.load(); // Load the new source

      // *** REMOVED AUTOPLAY *** - This prevents interference

      // Ensure the UI state reflects paused status after loading new template
      setIsPlaying(false);
    }
  };
  // --- END: Minimal Fix 2 ---

  // Handle template selection

  const handleTemplateSelect = (template) => {
    // Just call the preview function which now also selects the template
    handleTemplatePreview(template);
  };

  const handleDemoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingDemo(true);

    // Create FormData for upload
    const formData = new FormData();
    formData.append("demo_video", file);

    try {
      // Add progress tracking
      const response = await instance.post("api/upload-demo-video/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },

        // For handleDemoUpload function, right after setting progress:
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          // Update upload progress
          setUploadProgress(percentCompleted);
          console.log("Upload progress:", percentCompleted);
        },
      });

      // Set the demo video information
      setDemoVideo(response.data);

      // Enable demo video if it was disabled
      if (!includeDemoVideo) {
        setIncludeDemoVideo(true);
      }

      // If we have a video ref, load the new video
      if (videoRef.current && currentStep === 3) {
        videoRef.current.src = response.data.s3_url;
        videoRef.current.load();
      }
    } catch (error) {
      console.error("Failed to upload demo video:", error);
      alert("Failed to upload demo video. Please try again with a smaller file or different format.");
    } finally {
      setUploadingDemo(false);
      setUploadProgress(0);
    }
  };

  const addTimelineCaption = () => {
    if (!videoRef.current) return;

    // Create modal dialog for caption entry
    const dialog = document.createElement("div");
    dialog.className = "fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50";
    dialog.innerHTML = `
      <div class="bg-white rounded-2xl p-6 shadow-2xl w-full max-w-md transform transition-all">
        <h3 class="text-lg font-medium text-gray-900 mb-3">Add Timeline Caption</h3>
        <input 
          type="text"
          placeholder="What's happening in this moment?"
          class="w-full px-4 py-3 rounded-xl bg-gray-50 border-0 focus:ring-2 focus:ring-green-500"
          autofocus
        />
        
        <div class="mt-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Caption Position</label>
          <div class="flex items-center justify-center gap-4">
            <button data-position="top" class="p-2 rounded-lg hover:bg-green-50 transition-colors">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="m5 12 7-7 7 7"></path>
                <path d="M12 19V5"></path>
              </svg>
            </button>
            <button data-position="center" class="p-2 rounded-lg bg-green-50 hover:bg-green-100 transition-colors">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="21" x2="3" y1="6" y2="6"></line>
                <line x1="21" x2="3" y1="12" y2="12"></line>
                <line x1="21" x2="3" y1="18" y2="18"></line>
              </svg>
            </button>
            <button data-position="bottom" class="p-2 rounded-lg hover:bg-green-50 transition-colors">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="m19 12-7 7-7-7"></path>
                <path d="M12 5v14"></path>
              </svg>
            </button>
          </div>
        </div>
        
        <div class="mt-6 flex justify-end gap-3">
          <button id="cancel" class="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
          <button id="confirm" class="px-4 py-2 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600">
            Add Caption
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(dialog);
    const input = dialog.querySelector("input");
    let position = "center"; // Default position

    // Handle position buttons
    dialog.querySelectorAll("[data-position]").forEach((btn) => {
      btn.onclick = () => {
        position = btn.dataset.position;
        dialog.querySelectorAll("[data-position]").forEach((b) => b.classList.remove("bg-green-50"));
        btn.classList.add("bg-green-50");
      };
    });

    // dialog.querySelector("#cancel").onclick = () => dialog.remove();
    // dialog.querySelector("#confirm").onclick = () => {
    //   if (input.value.trim()) {
    //     const newCaption = {
    //       time: videoRef.current.currentTime,
    //       text: input.value.trim(),
    //       position: position,
    //     };
    //     setTimelineCaptions((prev) => [...prev, newCaption].sort((a, b) => a.time - b.time));
    //   }
    //   dialog.remove();
    // };

    dialog.querySelector("#cancel").onclick = () => {
      dialog.remove();
      // Resume playback after closing dialog
      if (videoRef.current) {
        videoRef.current
          .play()
          .then(() => {
            setIsPlaying(true);
            console.log("Playback resumed after canceling caption");
          })
          .catch((e) => console.warn("Could not resume playback:", e));
      }
    };

    dialog.querySelector("#confirm").onclick = () => {
      if (input.value.trim()) {
        const newCaption = {
          time: videoRef.current.currentTime,
          text: input.value.trim(),
          position: position,
        };
        setTimelineCaptions((prev) => [...prev, newCaption].sort((a, b) => a.time - b.time));
      }
      dialog.remove();
      // Resume playback after adding caption
      if (videoRef.current) {
        videoRef.current
          .play()
          .then(() => {
            setIsPlaying(true);
            console.log("Playback resumed after adding caption");
          })
          .catch((e) => console.warn("Could not resume playback:", e));
      }
    };
  };

  // Add this function to your component to preview captions with styling
  const renderTimelineCaptionPreview = (caption) => {
    if (!caption) return null;

    // Use the same styling as the hook caption
    const style = {
      fontFamily:
        selectedFont === "bebas"
          ? "'Bebas Neue', sans-serif"
          : selectedFont === "anton"
          ? "'Anton', sans-serif"
          : selectedFont === "montserrat"
          ? "'Montserrat', sans-serif"
          : "'Inter', sans-serif",
      fontSize: fontSize === "small" ? "1rem" : fontSize === "large" ? "1.75rem" : "1.25rem",
      backgroundColor: captionBg !== "transparent" ? captionBg : undefined,
      color: "white",
      padding: "0.25rem 0.5rem",
      borderRadius: "0.25rem",
      textShadow: "0 1px 3px rgba(0,0,0,0.8)",
    };

    return <p style={style}>{caption.text}</p>;
  };
  // Format time display (MM:SS)
  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  // Handle custom template upload
  const handleCustomTemplateUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Show loading state
    setTemplateLoading(true);

    // Create FormData for upload
    const formData = new FormData();
    formData.append("hook_video", file);

    try {
      // Make API call to upload custom template
      const response = await instance.post("api/upload-hook-video/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Create template object from response
      const customTemplate = {
        id: response.data.id,
        title: file.name.replace(/\.[^/.]+$/, "") || "My Custom Template",
        s3_url: response.data.s3_url,
        thumbnails: response.data.thumbnails,
        description: "Custom uploaded template",
        category: "Custom",
        isCustom: true,
      };

      // Add to templates list and select it
      setTemplates((prev) => [customTemplate, ...prev]);
      handleTemplateSelect(customTemplate);
    } catch (error) {
      console.error("Failed to upload custom template:", error);
      alert("Failed to upload video. Please try again with a smaller file or different format.");
    } finally {
      setTemplateLoading(false);
    }
  };

  // Toggle video playback
  // const togglePlayback = () => {
  //   if (!videoRef.current) return;

  //   if (videoRef.current.paused) {
  //     videoRef.current
  //       .play()
  //       .then(() => {
  //         setIsPlaying(true);
  //       })
  //       .catch((error) => {
  //         console.error("Video playback failed:", error);
  //       });
  //   } else {
  //     videoRef.current.pause();
  //     setIsPlaying(false);
  //   }
  // };

  // const togglePlayback = () => {
  //   if (!videoRef.current) return;

  //   console.log("Toggle playback called, current paused state:", videoRef.current.paused);

  //   if (videoRef.current.paused) {
  //     // Add loading indicator
  //     setIsPlaying(false); // Reset first to show loading state in UI

  //     videoRef.current
  //       .play()
  //       .then(() => {
  //         console.log("Video playback started successfully");
  //         setIsPlaying(true);
  //       })
  //       .catch((error) => {
  //         console.error("Video playback failed:", error);
  //         // Show a user-friendly notification
  //         showNotification("Playback Error", "Unable to play video. Please try again.", "error");
  //         setIsPlaying(false);
  //       });
  //   } else {
  //     videoRef.current.pause();
  //     setIsPlaying(false);
  //     console.log("Video paused");
  //   }
  // };
  // --- START: Minimal Fix 1: Correct togglePlayback ---
  const togglePlayback = () => {
    if (!videoRef.current) {
      console.warn("Video ref not available for playback toggle.");
      return;
    }

    if (videoRef.current.paused) {
      // If paused, attempt to play it
      console.log("Toggle: Attempting to play...");
      videoRef.current.play().catch((error) => {
        console.error("Video playback failed on toggle:", error);
        showNotification("Playback Error", "Unable to play video.", "error");
        // Rely on onPause event to correct state if play fails
      });
    } else {
      // If playing, pause it
      console.log("Toggle: Attempting to pause...");
      videoRef.current.pause();
      // Rely on onPause event to set state
    }
  };
  // --- END: Minimal Fix 1 ---

  // Add to your useEffect hooks
  useEffect(() => {
    // Recover task/project IDs from localStorage if available
    const savedTaskId = localStorage.getItem("videoTaskId");
    const savedProjectId = localStorage.getItem("videoProjectId");

    if (savedTaskId && savedProjectId) {
      setTaskId(savedTaskId);
      setProjectId(savedProjectId);

      // Check if the task is still processing
      instance
        .get(`api/video-status/${savedProjectId}/`)
        .then((response) => {
          if (!response.data.is_processed) {
            // Resume UI for in-progress video
            setProcessing(true);
            simulateProcessingSteps();
            setTimeout(checkVideoStatus, 1000);
          } else if (response.data.processed_video_url) {
            // Video is already done
            setFinalVideoUrl(response.data.processed_video_url);
          }
        })
        .catch((error) => {
          console.error("Error checking saved task:", error);
          // Clear invalid storage
          localStorage.removeItem("videoTaskId");
          localStorage.removeItem("videoProjectId");
        });
    }
  }, []);

  // Add this function to your VideoCreator component
  const checkVideoStatus = async (projectId) => {
    if (!projectId) return;

    console.log("Checking status for project:", projectId);

    try {
      // Force a fresh check from the server
      const response = await instance.get(`api/video-status/${projectId}/`, {
        params: { force: "true" }, // Add force parameter to ensure fresh check
      });

      console.log("Status check response:", response.data);

      // Update UI with current status
      if (response.data.status) {
        setProcessingStep(response.data.status);
      }

      // Calculate progress based on status
      if (response.data.status) {
        const progressMappings = {
          Preparing: 10,
          "Adding hook": 30,
          "Processing demo": 50,
          "Adding captions": 70,
          "Adding music": 85,
          Finalizing: 95,
          Complete: 100,
        };

        // Set progress based on status text
        for (const [keyword, progress] of Object.entries(progressMappings)) {
          if (response.data.status.includes(keyword)) {
            setProcessingProgress(progress);
            break;
          }
        }
      }

      // Handle completed video
      if (response.data.is_processed && response.data.processed_video_url) {
        setFinalVideoUrl(response.data.processed_video_url);
        setProcessingProgress(100);
        setProcessingStep("Complete! Your video is ready.");

        // Update thumbnail if available
        if (response.data.thumbnail_url) {
          console.log("Thumbnail available:", response.data.thumbnail_url);
        }

        setTimeout(() => {
          setShowFinalVideo(true);
          setProcessing(false);

          // Refresh video library
          fetchUserProcessedVideos();
        }, 1000);

        return true;
      }
      // Handle error
      else if (response.data.error) {
        console.error("Processing error:", response.data.error);
        showNotification("Error", "Error processing video: " + response.data.error, "error");
        setProcessing(false);
        return false;
      }
      // Still processing
      else {
        return false;
      }
    } catch (error) {
      console.error("Error checking status:", error);
      showNotification("Connection Error", "Failed to check video status. Please try again.", "error");
      return false;
    }
  };

  const simulateProcessingSteps = () => {
    const steps = [
      { progress: 10, message: "Preparing video files..." },
      { progress: 25, message: "Adding hook caption..." },
      { progress: 40, message: "Processing demo video..." },
      { progress: 60, message: "Adding timeline captions..." },
      { progress: 75, message: "Adding background music..." },
      { progress: 90, message: "Finalizing your video..." },
    ];

    let currentStep = 0;

    const updateStep = () => {
      if (currentStep < steps.length && !showFinalVideo) {
        setProcessingStep(steps[currentStep].message);
        setProcessingProgress(steps[currentStep].progress);
        currentStep++;
        setTimeout(updateStep, 5000 + Math.random() * 3000); // Random time between 5-8 seconds
      }
    };

    updateStep();
  };

  // Handle timeline scrubbing
  const handleTimelineScrub = (e) => {
    if (!videoRef.current || !progressBarRef.current) return;

    const rect = progressBarRef.current.getBoundingClientRect();
    const clickPosition = e.clientX - rect.left;
    const percentage = clickPosition / rect.width;

    videoRef.current.currentTime = percentage * videoRef.current.duration;
  };

  // Fixed showProcessingSuccessModal function to handle DOM properly
  const showProcessingSuccessModal = (projectId) => {
    // Create modal element
    const modalEl = document.createElement("div");
    modalEl.className = "fixed inset-0 z-50 flex items-center justify-center";
    modalEl.innerHTML = `
      <div class="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
      <div class="relative bg-white rounded-2xl w-full max-w-md mx-4 shadow-xl p-6">
        <div class="text-center">
          <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 class="text-xl font-semibold text-gray-900 mb-2">Video Processing Started!</h3>
          <p class="text-gray-600 mb-6">Your video has been sent for processing. This typically takes 2-5 minutes to complete.</p>
          
          <div class="space-y-4">
            <div class="bg-blue-50 rounded-lg p-4 border border-blue-100 text-left">
              <p class="text-blue-800">Your video will appear in your library once it's ready. You can continue working on other tasks while it processes.</p>
            </div>
            
            <div class="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="/video-library" class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-center flex items-center justify-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Go to My Videos
              </a>
              <button id="create-another-btn" class="px-4 py-2 border border-gray-200 bg-white text-gray-700 rounded-lg hover:bg-gray-50 text-center">
                Create Another Video
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(modalEl);

    // Add event listeners after appending to DOM
    const createAnotherBtn = modalEl.querySelector("#create-another-btn");
    if (createAnotherBtn) {
      createAnotherBtn.addEventListener("click", () => {
        // Reset the form
        setCurrentStep(1);
        setProcessing(false);
        setShowFinalVideo(false);
        setTimelineCaptions([]);
        setDemoVideo(null);

        // Remove the modal
        modalEl.remove();
      });
    }

    // Auto remove after 30 seconds
    setTimeout(() => {
      if (document.body.contains(modalEl)) {
        modalEl.remove();
      }
    }, 30000);

    // Reset processing UI state
    setProcessing(false);
  };

  const CreditsBadge = ({ credits }) => {
    return (
      <div className="relative group">
        <div
          className={`bg-${credits > 0 ? "green" : "red"}-100 text-${
            credits > 0 ? "green" : "red"
          }-800 py-1 px-3 rounded-full text-sm flex items-center`}
        >
          <Video className="w-4 h-4 mr-1" />
          <span>
            {credits} credit{credits !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Tooltip */}
        <div
          className="absolute z-50 w-64 p-3 bg-white rounded-lg shadow-xl border border-gray-200 text-sm text-gray-700
                        opacity-0 invisible group-hover:opacity-100 group-hover:visible
                        transition-all duration-200 bottom-full mb-2 left-1/2 transform -translate-x-1/2"
        >
          {credits > 0 ? (
            <>
              <p className="font-medium">
                You have {credits} video credit{credits !== 1 ? "s" : ""} available
              </p>
              <p className="text-xs mt-1">Each credit allows you to create one video</p>
            </>
          ) : userCredits.hasPreviewAccess ? (
            <>
              <p className="font-medium">You can create one free preview video</p>
              <p className="text-xs mt-1">Upgrade your plan for more credits</p>
            </>
          ) : (
            <>
              <p className="font-medium">You're out of credits</p>
              <p className="text-xs mt-1">Upgrade your plan to create more videos</p>
            </>
          )}

          <div
            className="absolute w-3 h-3 bg-white border-t border-l border-gray-200 transform rotate-45
                          left-1/2 -bottom-1.5 -ml-1.5"
          ></div>
        </div>
      </div>
    );
  };

  // 2. Enhanced handleCreateVideo function with improved credit & font handling
  const handleCreateVideo = async () => {
    // Validation checks
    if (!selectedTemplate) {
      showNotification("Missing Template", "Please select a template", "error");
      return;
    }

    if (!hookCaption.trim()) {
      showNotification("Missing Caption", "Please add a hook caption", "error");
      return;
    }

    if (!backgroundMusic) {
      showNotification("Missing Music", "Please select background music", "error");
      return;
    }

    // Credit check - Show user-friendly message if no credits
    if (userCredits.video_credits <= 0 && !userCredits.hasPreviewAccess) {
      if (userCredits.subscription_active) {
        showNotification(
          "No Credits",
          `You've used all your video credits for this billing cycle. Your plan (${userCredits.plan_name}) will refresh credits on your next billing date.`,
          "error"
        );
      } else {
        showNotification("No Credits", "You don't have any video credits left. Please upgrade your plan to create more videos.", "error");
      }
      return;
    }

    // Reset any previous final video URL to prevent showing old videos
    // setFinalVideoUrl(null);
    // setShowFinalVideo(false);

    // Show processing state
    // setProcessing(true);
    // simulateProcessingSteps(); // Show immediate feedback

    try {
      // Create a properly structured caption object with complete styling
      const captionStyle = {
        text: hookCaption,
        position: captionPosition,
        font: selectedFont, // Important: Include font selection
        fontSize: fontSize,
        background: captionBg,
      };

      // Apply the same styling options to each timeline caption
      const styledTimelineCaptions = timelineCaptions.map((caption) => ({
        ...caption,
        // Keep the caption's original position and text
        // But ensure it inherits the font and background styling from the hook caption
        font: selectedFont, // Important: Include font selection
        fontSize: fontSize,
        background: captionBg,
      }));

      const payload = {
        // Hook video with caption and styling
        hook_video_url: selectedTemplate.s3_url,
        hook_caption: captionStyle,
        // Demo video with timeline captions (if included)
        demo_video_url: includeDemoVideo && demoVideo ? demoVideo.s3_url : null,
        timeline_captions: includeDemoVideo ? styledTimelineCaptions : [],
        // Selected background music
        background_music_url: backgroundMusic.url,
      };

      console.log("Sending video creation request with payload:", JSON.stringify(payload));

      // Call the API endpoint
      const response = await instance.post("api/process-video/", payload);
      console.log("Received video creation response:", response.data);

      if (response.data && response.data.project_id) {
        // Store the project ID
        setProjectId(response.data.project_id);

        // Update state based on response
        if (response.data.task_id) {
          console.log(`Task started with ID: ${response.data.task_id}`);
          setTaskId(response.data.task_id);

          // Store in localStorage for recovery
          localStorage.setItem("videoTaskId", response.data.task_id);
          localStorage.setItem("videoProjectId", response.data.project_id);
        }

        // Immediately update displayed credits (optimistic update)
        setUserCredits((prev) => ({
          ...prev,
          video_credits: Math.max(0, prev.video_credits - 1),
          hasPreviewAccess: false, // Mark preview as used
        }));

        // Show success modal
        showProcessingSuccessModal(response.data.project_id);
      } else {
        // Handle unexpected response format
        console.error("Unexpected response format:", response.data);
        throw new Error("Received invalid response from server");
      }
    } catch (error) {
      console.error("Failed to create video:", error);
      setProcessing(false);

      // Provide more detailed error messages based on the error
      if (error.response) {
        console.error("Server response error:", error.response.data);

        // Custom handling for credit-related errors
        if (error.response.data?.message?.includes("no video credits") || error.response.status === 402) {
          showNotification(
            "No Credits Available",
            "You don't have any video credits left. Please upgrade your plan to create more videos.",
            "error"
          );
        } else {
          showNotification("Server Error", error.response.data.message || "Unknown server error", "error");
        }
      } else if (error.request) {
        console.error("No response received:", error.request);
        showNotification("Connection Error", "Server not responding. Please try again later.", "error");
      } else {
        showNotification("Error", "Failed to create video: " + error.message, "error");
      }
    }
  };

  // 3. Enhanced styling for Caption component in Step 2
  // Fix the font selection issue with proper styling application
  // Add this to the Step 2 component return JSX:

  // Function to get CSS font-family string based on the selectedFont state
  const getFontFamily = (font) => {
    // Pass the font state ('selectedFont') when calling this
    switch (font) {
      case "bebas":
        return "'Bebas Neue', sans-serif";
      case "anton":
        return "'Anton', sans-serif";
      case "montserrat":
        return "'Montserrat', sans-serif";
      case "inter": // Assuming 'inter' is a valid option you might add
        return "'Inter', sans-serif";
      default:
        return "'Montserrat', sans-serif"; // Sensible default
    }
  };

  // Function to get CSS font-size value based on the fontSize state
  const getFontSize = (size) => {
    // Pass the size state ('fontSize') when calling this
    switch (size) {
      case "small":
        return "0.85rem"; // Smaller
      case "large":
        return "1.35rem"; // Reduced large size
      default: // 'medium' or any other fallback
        return "1.1rem"; // Reduced medium size
    }
  };

  // Helper function to trigger Uppy file selection
  // NOTE: This implementation is a basic attempt. The exact selector
  // '.uppy-Dashboard-trigger' might need to be changed depending on how Uppy
  // renders its button in your specific setup (e.g., inside your UppyUploader component).
  // Inspect the Uppy button in your browser's developer tools to find the right class or ID.
  const triggerUppyUpload = () => {
    console.log("Attempting to trigger Uppy upload via '+' button...");
    // Common Uppy trigger class, might need adjustment
    const uppyTriggerButton = document.querySelector(".uppy-Dashboard-trigger");
    if (uppyTriggerButton && uppyTriggerButton instanceof HTMLElement) {
      console.log("Uppy trigger button found, clicking...");
      uppyTriggerButton.click();
    } else {
      // Fallback if the primary trigger isn't found - maybe Uppy uses a different structure?
      console.warn("Uppy trigger button (.uppy-Dashboard-trigger) not found or not an HTMLElement.");
      // As a last resort, tell the user and maybe scroll to the main uploader
      showNotification("Info", "Please use the main upload area below to add your video.", "info");
      const uppySection = document.getElementById("uppy-upload-section");
      if (uppySection) {
        uppySection.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  const renderHookCaption = () => {
    // Convert font name to CSS font-family
    const getFontFamily = () => {
      switch (selectedFont) {
        case "bebas":
          return "'Bebas Neue', sans-serif";
        case "anton":
          return "'Anton', sans-serif";
        case "montserrat":
          return "'Montserrat', sans-serif";
        default:
          return "'Inter', sans-serif";
      }
    };

    // Get font size CSS value
    const getFontSize = () => {
      switch (fontSize) {
        case "small":
          return "1rem";
        case "large":
          return "1.75rem";
        default:
          return "1.25rem";
      }
    };

    return (
      <div
        className={`absolute inset-x-0 ${
          captionPosition === "top" ? "top-8" : captionPosition === "center" ? "top-1/2 -translate-y-1/2" : "bottom-8"
        } px-6 text-center z-20`}
      >
        <p
          className="text-white font-normal inline-block"
          style={{
            backgroundColor: captionBg === "transparent" ? "transparent" : captionBg,
            fontFamily: getFontFamily(),
            fontSize: getFontSize(),
            textShadow: "0 1px 2px rgba(0,0,0,0.9)",
            padding: captionBg === "transparent" ? "0" : "0.25rem 0.5rem",
            borderRadius: captionBg === "transparent" ? "0" : "0.25rem",
          }}
        >
          {hookCaption || "Add your caption"}
        </p>
      </div>
    );
  };

  useEffect(() => {
    // If we're back at step 1, we should clear any final video state
    if (currentStep === 1) {
      setShowFinalVideo(false);
      setFinalVideoUrl(null);
      setProcessing(false);
    }
  }, [currentStep]);

  function directPlay() {
    console.log("DIRECT PLAY CLICKED");
    if (videoRef.current) {
      console.log("Video element exists, attempting to play");
      videoRef.current.play().catch((e) => console.log("Play error:", e));
    } else {
      console.log("No video element found");
    }
  }

  // Direct pause function - simple as possible
  function directPause() {
    console.log("DIRECT PAUSE CLICKED");
    if (videoRef.current) {
      videoRef.current.pause();
    }
  }

  // Then modify your session recovery function to be more selective
  const recoverVideoSession = async () => {
    // Only try to recover session if we're at step 1 (initial state)
    if (currentStep !== 1) {
      return;
    }

    const savedTaskId = localStorage.getItem("videoTaskId");
    const savedProjectId = localStorage.getItem("videoProjectId");

    if (savedTaskId && savedProjectId) {
      try {
        const response = await instance.get(`api/video-status/${savedProjectId}/`);

        // Only recover and show completed video if it's actually complete
        if (response.data.is_processed && response.data.processed_video_url) {
          setTaskId(savedTaskId);
          setProjectId(savedProjectId);
          setFinalVideoUrl(response.data.processed_video_url);

          // Show notification instead of modal
          showNotification("Video Ready", "Your recently processed video is ready to view.", "success");

          // Don't automatically show the modal - let user continue their new video
          // setShowFinalVideo(true);

          // Show button to view previous video
          setTimeout(() => {
            // Create a floating button to view the previous video
            const viewBtn = document.createElement("div");
            viewBtn.className = "fixed bottom-4 right-4 z-40";
            viewBtn.innerHTML = `
              <button id="view-last-video" class="px-4 py-2 bg-green-600 text-white rounded-lg shadow-lg hover:bg-green-700 flex items-center gap-2">
                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                View Your Last Video
              </button>
            `;

            document.body.appendChild(viewBtn);

            // Add click handler
            document.getElementById("view-last-video").addEventListener("click", () => {
              setShowFinalVideo(true);
              viewBtn.remove();
            });

            // Auto remove after 15 seconds
            setTimeout(() => {
              if (document.body.contains(viewBtn)) {
                viewBtn.remove();
              }
            }, 15000);
          }, 1000);
        } else {
          // If video processing was started but not completed, just clear the localStorage
          localStorage.removeItem("videoTaskId");
          localStorage.removeItem("videoProjectId");
        }
      } catch (error) {
        console.error("Failed to recover session:", error);
        // Clear invalid storage
        localStorage.removeItem("videoTaskId");
        localStorage.removeItem("videoProjectId");
      }
    }
  };

  const renderStepContent = () => {
    // --- Assumed Parent Component Scope (Ensure these exist) ---
    // State: selectedFont, fontSize, captionBg, hookCaption, captionPosition, selectedCategory, currentPage, templates, totalPages, templateLoading, selectedTemplate, backgroundMusic, musicTracks, isPlaying, includeDemoVideo, demoVideo, userDemoVideos, demoVideoLoading, timelineCaptions, currentTime, duration, userCredits, processing, showDemoFaq
    // Setters: setSelectedFont, setFontSize, setCaptionBg, setHookCaption, setCaptionPosition, setSelectedCategory, setCurrentPage, setSelectedTemplate, setBackgroundMusic, setIsPlaying, setIncludeDemoVideo, setDemoVideo, setDemoVideoLoading, setTimelineCaptions, setShowDemoFaq
    // Refs: videoRef, progressBarRef
    // Functions: handleTemplateSelect, fetchDemoVideos, addTimelineCaption, formatTime, togglePlayback, handleTimelineScrub, handleCreateVideo, getFontFamily, getFontSize, triggerUppyUpload
    // Components: CreditsBadge, UppyUploader, Link (and Lucide icons)
    // -------------------------------------------------------------

    return (
      // Main Layout Grid - Increased gap for spaciousness
      <div className="flex justify-center items-center min-h-screen w-full">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr),minmax(0,1fr)] gap-6 lg:gap-8 ">
            {/* --- Left Column: Configuration Sections --- */}
            {/* Increased vertical spacing */}
            <div className="space-y-5">
              {/* === 1. Write Your Hook === */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Section Header with Clean Styling */}
                <div className="px-5 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex-grow">
                    <h3 className="text-lg font-semibold text-gray-900">1. Write Your Hook</h3>
                    <p className="text-sm text-gray-500 mt-0.5">Grab attention with a high-converting opener.</p>
                  </div>

                  {/* Position Buttons - Refined */}
                  <div className="flex items-center gap-0.5 bg-gray-50 p-0.5 rounded-lg flex-shrink-0 self-start sm:self-center">
                    {[
                      { icon: ArrowUp, value: "top", label: "Top" },
                      { icon: AlignCenter, value: "center", label: "Center" },
                      { icon: ArrowDown, value: "bottom", label: "Bottom" },
                    ].map(({ icon: Icon, value, label }) => (
                      <button
                        key={value}
                        onClick={() => setCaptionPosition(value)}
                        className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 transition-colors ${
                          captionPosition === value ? "bg-white text-green-600 shadow-sm font-medium" : "text-gray-600 hover:text-gray-800"
                        }`}
                        title={`Position: ${label}`}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <span className="hidden sm:inline">{label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Content Area with Better Spacing */}
                <div className="p-5">
                  {/* Improved Textarea */}
                  <textarea
                    value={hookCaption}
                    onChange={(e) => setHookCaption(e.target.value)}
                    className="w-full h-24 bg-gray-50 rounded-lg p-3.5 text-base resize-none border border-gray-200 focus:border-green-500 focus:ring-1 focus:ring-green-500/30 transition-all placeholder-gray-400"
                    placeholder="e.g., Stop doing X, try this instead..."
                  />

                  {/* Font & Style Controls - More Compact */}
                  <div className="mt-6 pt-5 border-t border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-800">Caption Style</h4>
                      <span className="text-xs text-gray-400">Applies to all captions</span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="font-type" className="block text-xs font-medium text-gray-600 mb-1.5">
                          Font
                        </label>
                        <select
                          id="font-type"
                          value={selectedFont}
                          onChange={(e) => setSelectedFont(e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:border-green-500 focus:ring-1 focus:ring-green-500/30 shadow-sm appearance-none"
                        >
                          <option value="montserrat">Montserrat</option>
                          <option value="inter">Inter</option>
                          <option value="bebas">Bebas Neue</option>
                          <option value="anton">Anton</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="font-size" className="block text-xs font-medium text-gray-600 mb-1.5">
                          Size
                        </label>
                        <select
                          id="font-size"
                          value={fontSize}
                          onChange={(e) => setFontSize(e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:border-green-500 focus:ring-1 focus:ring-green-500/30 shadow-sm appearance-none"
                        >
                          <option value="small">Small</option>
                          <option value="medium">Medium</option>
                          <option value="large">Large</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* === 2. Choose Hook Scene === */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">2. Choose a Hook Scene</h3>
                    <p className="text-sm text-gray-500 mt-0.5">
                      Select a high-performing clip to match your message.Real, relatable faces. Hyper-realistic AI actors.
                    </p>
                  </div>

                  <select
                    value={selectedCategory}
                    onChange={(e) => {
                      setSelectedCategory(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:border-green-500 focus:ring-1 focus:ring-green-500/30 shadow-sm appearance-none w-full md:w-auto"
                  >
                    {categories.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Template Gallery with Enhanced Grid */}
                <div className="p-5">
                  {templateLoading ? (
                    <div className="h-52 flex flex-col items-center justify-center text-gray-500 bg-gray-50 rounded-lg">
                      <Loader2 className="w-6 h-6 animate-spin mb-2 text-green-500" />
                      <span className="text-sm">Loading Templates...</span>
                    </div>
                  ) : templates && templates.length > 0 ? (
                    <div className="grid grid-cols-4 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-12 gap-2">
                      {templates.map((template) => (
                        <button
                          key={template.id}
                          onClick={() => handleTemplateSelect(template)}
                          className={`relative aspect-[9/16] rounded-lg overflow-hidden transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 ${
                            selectedTemplate?.id === template.id
                              ? "ring-2 ring-green-500 shadow-md"
                              : "ring-1 ring-gray-200 hover:ring-green-400"
                          }`}
                          aria-label={`Select template: ${template.title || "Template"}`}
                          aria-pressed={selectedTemplate?.id === template.id}
                        >
                          <img
                            src={template.thumbnails?.mobile || "placeholder.jpg"}
                            alt={template.title || "Template thumbnail"}
                            className="w-full h-full object-cover"
                          />

                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center">
                              <Play className="w-4 h-4 sm:w-5 sm:h-5 text-black ml-0.5" />
                            </div>
                          </div>

                          {selectedTemplate?.id === template.id && (
                            <div className="absolute top-1 right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="h-40 flex flex-col items-center justify-center text-gray-500 bg-gray-50 rounded-lg">
                      <Video className="w-8 h-8 mb-2 text-gray-300" />
                      <p className="text-sm">
                        No templates found in "{categories.find((c) => c.value === selectedCategory)?.label || "this category"}"
                      </p>
                    </div>
                  )}

                  {/* Refined Pagination Controls */}
                  {totalPages > 1 && (
                    <div className="flex justify-center items-center gap-3 mt-6 pt-4 border-t border-gray-100">
                      <button
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1 || templateLoading}
                        className="px-4 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 disabled:opacity-40 transition-colors focus:outline-none"
                      >
                        Previous
                      </button>
                      <span className="text-xs text-gray-500 font-medium">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages || templateLoading}
                        className="px-4 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 disabled:opacity-40 transition-colors focus:outline-none"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* === 3. Select Music === */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">3. Add Background Music</h3>
                    <p className="text-sm text-gray-500 mt-0.5">Choose a background track.</p>
                  </div>

                  {backgroundMusic && (
                    <button
                      onClick={() => {
                        const audio = document.getElementById("music-preview");
                        if (audio) audio.paused ? audio.play().catch((e) => console.error(e)) : audio.pause();
                      }}
                      className="p-2 rounded-full bg-gray-50 text-gray-600 hover:bg-gray-100 transition-colors focus:outline-none"
                      aria-label={isMusicPlaying ? "Pause music preview" : "Play music preview"}
                    >
                      {isMusicPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </button>
                  )}
                </div>

                <div className="p-5">
                  <audio
                    id="music-preview"
                    className="hidden"
                    src={backgroundMusic?.url}
                    onPlay={() => setIsMusicPlaying(true)}
                    onPause={() => setIsMusicPlaying(false)}
                    onEnded={() => setIsMusicPlaying(false)}
                  />

                  {/* Elegant Music Selection Cards */}
                  <div className="space-y-2">
                    {musicTracks && musicTracks.length > 0 ? (
                      musicTracks.map((track) => (
                        <button
                          key={track.id}
                          onClick={() => {
                            setBackgroundMusic(track);
                            const audio = document.getElementById("music-preview");
                            if (audio) {
                              if (audio.src !== track.url) audio.src = track.url;
                              audio.play().catch((e) => console.error(e));
                            }
                          }}
                          className={`w-full flex items-center gap-3 p-2.5 rounded-lg transition-all text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-green-500 ${
                            backgroundMusic?.id === track.id ? "bg-gray-50 ring-1 ring-green-500" : "hover:bg-gray-50/50"
                          }`}
                          aria-pressed={backgroundMusic?.id === track.id}
                        >
                          <div
                            className={`w-8 h-8 flex items-center justify-center rounded-full ${
                              backgroundMusic?.id === track.id ? "bg-green-500 text-white" : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            <Music className="w-4 h-4" />
                          </div>
                          <span className="text-sm text-gray-800 truncate">{track.title}</span>
                          {backgroundMusic?.id === track.id && <CheckCircle2 className="w-4 h-4 text-green-600 ml-auto" />}
                        </button>
                      ))
                    ) : (
                      <div className="py-8 text-center text-sm text-gray-400 bg-gray-50 rounded-lg">Loading music tracks...</div>
                    )}
                  </div>
                </div>
              </div>

              {/* === 4. Add Demo Video === */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">4. Plug in Your Demo</h3>
                      <p className="text-sm text-gray-500 mt-0.5">
                        Add a product clip or screen recording.If skipped, your video will auto-generate with hook scene + music.
                      </p>
                    </div>
                    <button
                      onClick={() => setShowDemoFaq(!showDemoFaq)}
                      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                      aria-label="Show demo tips"
                      aria-expanded={showDemoFaq}
                    >
                      <Info className="w-4 h-4" />
                    </button>
                    <p className="text-sm text-gray-500">(Optional)</p>
                  </div>

                  {/* Refined Toggle Switch */}
                  <label className="flex items-center cursor-pointer">
                    <span className="mr-3 text-sm text-gray-600">Include Demo</span>
                    <div className="relative">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={includeDemoVideo}
                        onChange={(e) => setIncludeDemoVideo(e.target.checked)}
                      />
                      <div className="w-10 h-5 bg-gray-200 rounded-full peer peer-checked:bg-green-500"></div>
                      <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-all peer-checked:translate-x-5"></div>
                    </div>
                  </label>
                </div>

                <div className="p-5">
                  {/* Tips Section - Slimmer */}
                  {showDemoFaq && (
                    <div className="mb-5 p-4 bg-gray-50 border border-gray-100 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="text-sm font-medium text-gray-700">Tips for Great Demo Videos</h4>
                        <button onClick={() => setShowDemoFaq(false)} className="text-gray-400 hover:text-gray-600" aria-label="Hide tips">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <ul className="space-y-1.5 text-xs text-gray-600 list-disc list-inside pl-1">
                        <li>
                          <strong>Short & Focused:</strong> 15-30 secs showing core value
                        </li>
                        <li>
                          <strong>Vertical Format:</strong> 9:16 ratio for social platforms
                        </li>
                        <li>
                          <strong>Clear Visuals:</strong> Good lighting, readable text
                        </li>
                        <li>
                          <strong>Show, Don't Tell:</strong> Demonstrate key actions
                        </li>
                        <li>
                          <strong>Optimize File:</strong> MP4, under 30-50MB
                        </li>
                      </ul>
                    </div>
                  )}

                  {/* Demo Video Content */}
                  {includeDemoVideo && (
                    <div className="space-y-5">
                      {demoVideo ? (
                        <>
                          {/* Selected Video Info - Cleaner */}
                          <div className="flex flex-col sm:flex-row items-start gap-4 p-4 bg-gray-50 border border-gray-200 rounded-lg relative">
                            <div className="w-20 h-36 rounded-md overflow-hidden bg-gray-100 flex-shrink-0 mx-auto sm:mx-0">
                              <img
                                src={demoVideo.thumbnails?.mobile || "placeholder.jpg"}
                                className="w-full h-full object-cover"
                                alt="Selected demo"
                              />
                            </div>
                            <div className="flex-1 min-w-0 flex flex-col justify-between py-1 w-full sm:w-auto">
                              <div>
                                <p
                                  className="text-sm font-medium text-gray-800 truncate mb-1"
                                  title={demoVideo.title || `Demo Video ${demoVideo.id}`}
                                >
                                  {demoVideo.title || `Demo Video`}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Duration: {demoVideo.metadata?.duration ? formatTime(demoVideo.metadata.duration) : "N/A"}
                                </p>
                              </div>
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <p className="text-xs text-gray-500 mb-1">Current Time:</p>
                                <p className="text-sm font-mono font-medium text-gray-700 mb-2">
                                  {formatTime(currentTime)}
                                  <span className="text-xs text-gray-500 font-normal"> / {formatTime(duration || 0)}</span>
                                </p>
                                <button
                                  onClick={() => {
                                    if (videoRef.current) {
                                      videoRef.current.pause();
                                      setIsPlaying(false);
                                    }
                                    addTimelineCaption();
                                  }}
                                  className="text-xs font-medium text-green-600 flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-md border border-gray-200 hover:border-green-400 transition-colors focus:outline-none"
                                  title={`Add caption at ${formatTime(currentTime)}`}
                                >
                                  <Plus className="w-3.5 h-3.5" />
                                  <span>Add Caption Here</span>
                                </button>
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                setDemoVideo(null);
                                setTimelineCaptions([]);
                                if (videoRef.current && selectedTemplate?.s3_url) {
                                  videoRef.current.src = selectedTemplate.s3_url;
                                  videoRef.current.load();
                                  videoRef.current.play().catch((e) => console.warn(e));
                                }
                              }}
                              className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
                              title="Remove Demo Video"
                              aria-label="Remove Demo Video"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>

                          {/* Timeline Captions */}
                          {timelineCaptions.length > 0 && (
                            <div className="pt-4 border-t border-gray-100">
                              <h4 className="text-sm font-medium text-gray-700 mb-3">Timeline Captions ({timelineCaptions.length})</h4>
                              <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
                                {timelineCaptions.map((caption, i) => (
                                  <div key={i} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-md border border-gray-200 group">
                                    <button
                                      onClick={() => {
                                        if (videoRef.current) videoRef.current.currentTime = caption.time;
                                      }}
                                      className="text-xs font-mono text-gray-600 bg-white px-2 py-1 rounded border border-gray-200 hover:border-green-400 hover:text-green-600 w-16 text-center transition-colors"
                                      title="Jump to time"
                                    >
                                      {formatTime(caption.time)}
                                    </button>
                                    <p className="flex-1 text-xs text-gray-700 truncate" title={caption.text}>
                                      {caption.text}
                                    </p>
                                    <button
                                      onClick={() => {
                                        const nc = [...timelineCaptions];
                                        nc.splice(i, 1);
                                        setTimelineCaptions(nc);
                                      }}
                                      className="p-1 text-gray-400 hover:text-red-500 hover:bg-gray-100 rounded-full opacity-50 group-hover:opacity-100 transition-all"
                                      title="Remove caption"
                                      aria-label="Remove caption"
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {timelineCaptions.length === 0 && (
                            <div className="text-center py-3 border-t border-gray-100">
                              <p className="text-xs text-gray-500">Add captions at specific moments</p>
                            </div>
                          )}
                        </>
                      ) : (
                        // Demo Video Selection
                        <>
                          {/* Video Library */}
                          {!demoVideoLoading && userDemoVideos && userDemoVideos.length > 0 && (
                            <div className="pb-5 border-b border-gray-100">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-medium text-gray-700">Your Videos</h4>
                                <button
                                  onClick={fetchDemoVideos}
                                  disabled={demoVideoLoading}
                                  className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 disabled:opacity-50"
                                >
                                  <RefreshCw className={`w-3.5 h-3.5 ${demoVideoLoading ? "animate-spin" : ""}`} />
                                  Refresh
                                </button>
                              </div>

                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                <button
                                  onClick={triggerUppyUpload}
                                  className="aspect-video border border-dashed border-gray-300 text-gray-500 hover:border-green-500 hover:text-green-600 hover:bg-gray-50 transition-all rounded-lg flex flex-col items-center justify-center p-3 focus:outline-none"
                                  aria-label="Upload new demo video"
                                >
                                  <UploadCloud className="w-6 h-6 mb-1.5 text-gray-400" />
                                  <span className="text-xs font-medium">Upload New</span>
                                </button>

                                {userDemoVideos.map((video) => (
                                  <button
                                    key={video.id}
                                    onClick={() => {
                                      if (video && video.s3_url) {
                                        setDemoVideo(video);
                                        if (videoRef.current) {
                                          videoRef.current.src = video.s3_url;
                                          videoRef.current.load();
                                          videoRef.current.play().catch((e) => console.warn(e));
                                          setIsPlaying(true);
                                        }
                                      } else {
                                        console.error("Missing s3_url");
                                        showNotification("Error", "Could not load video.", "error");
                                      }
                                    }}
                                    className="relative aspect-video rounded-lg overflow-hidden border border-gray-200 group text-left hover:border-green-400 transition-all focus:outline-none"
                                    aria-label={`Select demo video: ${video.title || "Video"}`}
                                  >
                                    <img
                                      src={video.thumbnails?.mobile || "placeholder.jpg"}
                                      className="w-full h-full object-cover"
                                      alt="Demo thumbnail"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 flex items-end transition-opacity">
                                      <div className="p-2 w-full">
                                        <p className="text-xs font-medium text-white truncate">{video.title || `Video ${video.id}`}</p>
                                        <p className="text-xs text-gray-300 mt-0.5">
                                          {video.metadata?.duration ? formatTime(video.metadata.duration) : "--:--"}
                                        </p>
                                      </div>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {demoVideoLoading && (
                            <div className="py-8 text-center text-gray-500">
                              <Loader2 className="w-5 h-5 animate-spin inline-block mb-2" />
                              <p className="text-xs">Loading your videos...</p>
                            </div>
                          )}

                          {/* Separator */}
                          {!demoVideoLoading && userDemoVideos?.length > 0 && (
                            <div className="relative my-5">
                              <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-200" />
                              </div>
                              <div className="relative flex justify-center">
                                <span className="bg-white px-2 text-xs font-medium text-gray-500">OR</span>
                              </div>
                            </div>
                          )}

                          {/* Upload Section */}
                          <div id="uppy-upload-section">
                            {(userDemoVideos?.length === 0 || demoVideoLoading === false) && (
                              <h4 className="text-sm font-medium text-gray-700 mb-3">
                                {userDemoVideos?.length > 0 ? "Upload a new video" : "Upload your demo"}
                              </h4>
                            )}

                            <div className="border border-dashed border-gray-300 hover:border-green-500 transition-colors rounded-lg p-5 text-center bg-white hover:bg-gray-50/50 min-h-[140px] flex flex-col justify-center">
                              <UppyUploader
                                onUploadProgress={setUploadProgress}
                                onUploadDone={(data) => {
                                  if (data && data.s3_url) {
                                    setDemoVideo(data);
                                    setIncludeDemoVideo(true);
                                    if (videoRef.current) {
                                      videoRef.current.src = data.s3_url;
                                      videoRef.current.load();
                                      videoRef.current.play().catch((e) => console.warn(e));
                                      setIsPlaying(true);
                                    }
                                    fetchDemoVideos();
                                  } else {
                                    console.error("Missing s3_url");
                                    showNotification("Error", "Upload failed.", "error");
                                  }
                                  setUploadProgress(0);
                                }}
                              />

                              {uploadProgress > 0 && uploadProgress < 100 && (
                                <div className="mt-4">
                                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-green-500 transition-all duration-200"
                                      style={{ width: `${uploadProgress}%` }}
                                    />
                                  </div>
                                  <p className="text-xs text-gray-500 mt-2">Uploading... {uploadProgress}%</p>
                                </div>
                              )}

                              <p className="text-xs text-gray-400 mt-4">MP4, MOV, WEBM formats recommended. Max 30MB.</p>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Demo Skipped Message */}
                  {!includeDemoVideo && (
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-600 text-xs border border-gray-200 flex items-start gap-2">
                      <Info className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      <span>Demo video is skipped. Your final video will only use the selected hook scene and background music.</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* --- End Left Column --- */}

            {/* --- Right Column: Preview & Actions --- */}
            <div className="lg:sticky lg:top-20 lg:self-start space-y-4">
              {/* Video Preview Card - Premium Design */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl shadow-xl border border-gray-200/60 pt-5 pb-6 px-4 sm:px-5 relative overflow-hidden">
                {/* Stylish header with subtle gradient underline */}
                <h3 className="text-base font-semibold text-center text-gray-800 mb-4 sm:mb-5 pb-1 relative">
                  📺 Live Preview
                  <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-16 h-0.5 bg-gradient-to-r from-emerald-300 to-green-500"></span>
                </h3>

                {/* Phone mockup container with premium device styling - LARGER & RESPONSIVE */}
                <div className="w-full max-w-[320px] sm:max-w-[280px] mx-auto relative">
                  {/* Device frame with shadows and highlights */}
                  <div
                    className="aspect-[9/16] bg-black rounded-[1.75rem] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] overflow-hidden relative
                     before:absolute before:inset-0 before:rounded-[1.75rem] before:border-[6px] before:border-gray-800 before:z-20
                      after:absolute after:inset-0 after:rounded-[1.75rem] after:shadow-[inset_0_0_0.5px_rgba(255,255,255,0.2)] after:z-20"
                  >
                    {/* Phone notch and status bar */}
                    <div className="absolute top-0 inset-x-0 h-6 z-30 flex justify-center items-start">
                      <div className="w-24 h-5 bg-black rounded-b-xl flex items-end justify-center pb-0.5 px-2">
                        <div className="w-12 h-1 bg-gray-700 rounded-full"></div>
                      </div>
                      <div className="absolute top-1 right-4 flex space-x-1.5">
                        <Signal className="w-3 h-3 text-gray-400" />
                        <Wifi className="w-3 h-3 text-gray-400" />
                        <Battery className="w-3.5 h-3.5 text-gray-400" />
                      </div>
                    </div>

                    {/* Gloss overlay */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/[0.03] to-white/[0.05] z-10 pointer-events-none"></div>

                    {/* Video container */}

                    <video
                      ref={videoRef}
                      key={demoVideo?.s3_url || selectedTemplate?.s3_url || "no-video"}
                      className="w-full h-full object-cover block"
                      preload="auto"
                      loop
                      muted
                      playsInline
                      onTimeUpdate={(e) => {
                        setCurrentTime(e.target.currentTime);
                        setDuration(e.target.duration || 0);
                      }}
                      onLoadedData={(e) => {
                        console.log("Video loaded:", e.target.src);
                        setDuration(e.target.duration || 0);
                      }}
                      onPlay={() => {
                        console.log("Video play event fired");
                        setIsPlaying(true);
                      }}
                      onPause={() => {
                        console.log("Video pause event fired");
                        setIsPlaying(false);
                      }}
                      onError={(e) => {
                        console.error("Video error:", e.target.error);
                      }}
                      src={demoVideo?.s3_url || selectedTemplate?.s3_url || ""}
                    />
                    {/* <video
                      ref={videoRef}
                      key={demoVideo?.s3_url || selectedTemplate?.s3_url || "no-video"}
                      className="w-full h-full object-cover block"
                      preload="auto"
                      loop
                      muted
                      playsInline
                      onTimeUpdate={(e) => {
                        setCurrentTime(e.target.currentTime);
                        setDuration(e.target.duration || 0);
                      }}
                      onLoadedData={(e) => {
                        setDuration(e.target.duration || 0);
                        if (!videoRef.current?.paused) setIsPlaying(true);
                        else
                          e.target.play().catch((err) => {
                            console.log("Preview auto-play prevented:", err);
                            setIsPlaying(false);
                          });
                      }}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                      src={demoVideo?.s3_url || selectedTemplate?.s3_url || ""}
                    /> */}

                    {/* Caption Overlays - FIXED for truly transparent backgrounds */}
                    {(!demoVideo || timelineCaptions.length === 0) && selectedTemplate && hookCaption && (
                      <div
                        className={`absolute inset-x-0 z-20 px-3 py-5 text-center pointer-events-none transition-all duration-200 ${
                          captionPosition === "top" ? "top-10" : captionPosition === "center" ? "top-1/2 -translate-y-1/2" : "bottom-16"
                        }`}
                      >
                        <p
                          className="text-white inline-block leading-tight"
                          style={{
                            fontFamily: getFontFamily(selectedFont),
                            fontSize: getFontSize(fontSize),
                            textShadow: captionBg === "transparent" ? "0 1px 2px rgba(0,0,0,0.9), 0 0 4px rgba(0,0,0,0.5)" : "none",
                            backgroundColor: captionBg === "transparent" ? "transparent" : captionBg,
                            padding: captionBg === "transparent" ? "0" : "0.25rem 0.5rem",
                            borderRadius: captionBg === "transparent" ? "0" : "0.25rem",
                          }}
                        >
                          {hookCaption}
                        </p>
                      </div>
                    )}

                    {/* Timeline captions with proper transparency */}
                    {demoVideo &&
                      timelineCaptions.length > 0 &&
                      timelineCaptions
                        .filter((caption) => Math.abs(caption.time - currentTime) < 0.7)
                        .map((caption, i) => (
                          <div
                            key={i}
                            className={`absolute inset-x-0 z-20 px-3 text-center pointer-events-none transition-all duration-200 ${
                              caption.position === "top"
                                ? "top-8"
                                : caption.position === "center"
                                ? "top-1/2 -translate-y-1/2"
                                : "bottom-16"
                            }`}
                          >
                            <p
                              className="text-white inline-block leading-tight"
                              style={{
                                fontFamily: getFontFamily(selectedFont),
                                fontSize: getFontSize(fontSize),
                                textShadow: captionBg === "transparent" ? "0 1px 2px rgba(0,0,0,0.9), 0 0 4px rgba(0,0,0,0.5)" : "none",
                                backgroundColor: captionBg === "transparent" ? "transparent" : captionBg,
                                padding: captionBg === "transparent" ? "0" : "0.25rem 0.5rem",
                                borderRadius: captionBg === "transparent" ? "0" : "0.25rem",
                              }}
                            >
                              {caption.text}
                            </p>
                          </div>
                        ))}

                    {/* Redesigned Play/Pause Overlay - More elegant & responsive */}
                    {/* <div
                      className="absolute inset-0 flex items-center justify-center cursor-pointer focus:outline-none"
                      onClick={togglePlayback}
                      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && togglePlayback()}
                      tabIndex={0}
                      aria-label={isPlaying ? "Pause video" : "Play video"}
                    >
                      <div className={`transition-all duration-300 ease-out ${isPlaying ? "opacity-0 hover:opacity-100" : "opacity-100"}`}>
                        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center hover:scale-105 transition-all duration-200 shadow-lg hover:bg-black/60 border border-white/10">
                          {isPlaying ? (
                            <Pause className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                          ) : (
                            <Play className="w-5 h-5 sm:w-6 sm:h-6 text-white ml-0.5" />
                          )}
                        </div>
                      </div>
                    </div> */}
                    {/* 
                    <div
                      className="absolute inset-0 flex z-20 items-center justify-center cursor-pointer focus:outline-none"
                      onClick={() => {
                        console.log("Play button clicked");
                        togglePlayback();
                      }}
                      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && togglePlayback()}
                      tabIndex={0}
                      aria-label={isPlaying ? "Pause video" : "Play video"}
                    >
                      <div className={`transition-all duration-300 ease-out ${isPlaying ? "opacity-0 hover:opacity-100" : "opacity-100"}`}>
                        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center hover:scale-105 transition-all duration-200 shadow-lg hover:bg-black/60 border border-white/10">
                          {isPlaying ? (
                            <Pause className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                          ) : (
                            <Play className="w-5 h-5 sm:w-6 sm:h-6 text-white ml-0.5" />
                          )}
                        </div>
                      </div>
                    </div> */}

                    <div className="absolute inset-0 flex items-center justify-center z-40">
                      {isPlaying ? (
                        // Pause button
                        <button
                          type="button"
                          onClick={() => {
                            directPause();
                            setIsPlaying(false);
                          }}
                          style={{
                            width: "60px",
                            height: "60px",
                            borderRadius: "100%",
                            backgroundColor: "rgba(0,0,0,0.6)",
                            border: "none",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
                          }}
                        >
                          <Pause style={{ width: "24px", height: "24px" }} />
                        </button>
                      ) : (
                        // Play button
                        <button
                          type="button"
                          onClick={() => {
                            directPlay();
                            setIsPlaying(true);
                          }}
                          style={{
                            width: "60px",
                            height: "60px",
                            borderRadius: "100%",
                            backgroundColor: "rgba(0,0,0,0.6)",
                            border: "none",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
                          }}
                        >
                          <Play style={{ width: "24px", height: "24px", marginLeft: "4px" }} />
                        </button>
                      )}
                    </div>

                    {/* Improved Progress Bar */}
                    <div
                      className="absolute bottom-0 left-0 right-0 h-6 cursor-pointer z-30"
                      onClick={handleTimelineScrub}
                      ref={progressBarRef}
                    >
                      <div className="absolute bottom-0 left-0 right-0 bg-gray-800/40 backdrop-blur-[1px] h-1 hover:h-1.5 transition-all duration-200 overflow-hidden">
                        <div
                          className="absolute inset-y-0 left-0 bg-white h-full transition-transform duration-100 ease-linear"
                          style={{ transform: `scaleX(${currentTime / (duration || 1)})`, transformOrigin: "left" }}
                        />
                      </div>
                      <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 opacity-0 hover:opacity-100 transition-opacity duration-200 bg-black/80 text-white text-xs px-2 py-0.5 rounded backdrop-blur-sm pointer-events-none whitespace-nowrap">
                        {formatTime(currentTime)} / {formatTime(duration || 0)}
                      </div>
                    </div>
                  </div>

                  {/* Home indicator line */}
                  <div className="w-1/3 h-1 bg-gray-700 rounded-full mx-auto mt-2"></div>

                  {/* Current time indicator */}
                  <div className="text-center mt-3">
                    <span className="text-xs font-mono text-gray-500">
                      {formatTime(currentTime)} / {formatTime(duration || 0)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Card - Responsive */}
              <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-xl border border-gray-100/80 text-center">
                <div className="inline-flex justify-center mb-4">
                  <CreditsBadge credits={userCredits.video_credits} hasPreviewAccess={userCredits.hasPreviewAccess} />
                </div>

                <button
                  onClick={handleCreateVideo}
                  disabled={
                    processing ||
                    !selectedTemplate ||
                    !hookCaption ||
                    !backgroundMusic ||
                    (includeDemoVideo && !demoVideo) ||
                    (userCredits.video_credits <= 0 && !userCredits.hasPreviewAccess)
                  }
                  className={`w-full py-3 px-4 rounded-xl text-sm sm:text-base font-bold flex items-center justify-center gap-2 transition-all duration-200 shadow-lg active:shadow-md disabled:opacity-60 disabled:cursor-not-allowed ${
                    processing
                      ? "bg-gray-400 text-gray-600"
                      : userCredits.video_credits > 0 || userCredits.hasPreviewAccess
                      ? "bg-green-500 text-white hover:shadow-xl hover:brightness-110 transform hover:-translate-y-0.5"
                      : "bg-gradient-to-r from-red-500 to-rose-600 text-white"
                  }`}
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" /> Processing...
                    </>
                  ) : userCredits.video_credits > 0 || userCredits.hasPreviewAccess ? (
                    <>
                      <Zap className="w-4 h-4 sm:w-5 sm:h-5" /> Create Video
                      {userCredits.hasPreviewAccess && userCredits.video_credits <= 0 ? " (1 Free Preview)" : ""}
                    </>
                  ) : (
                    <>
                      <Gift className="w-4 h-4 sm:w-5 sm:h-5" /> Upgrade to Create
                    </>
                  )}
                </button>

                {!processing && (
                  <>
                    {userCredits.video_credits <= 0 && !userCredits.hasPreviewAccess && (
                      <p className="text-xs text-red-600 font-medium mt-3">
                        You need credits to create videos.{" "}
                        <a href="/credits" className="underline hover:text-red-700">
                          Upgrade Plan?
                        </a>
                      </p>
                    )}
                    {(userCredits.video_credits > 0 || userCredits.hasPreviewAccess) &&
                      (!selectedTemplate || !hookCaption || !backgroundMusic || (includeDemoVideo && !demoVideo)) && (
                        <p className="text-xs text-gray-500 mt-3">Please complete all required steps to enable video creation.</p>
                      )}
                  </>
                )}
              </div>
            </div>
            {/* --- End Right Column --- */}
          </div>
        </div>
      </div> // End Main Grid
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Sticky Header */}
      <EnhancedHeader />
      <header className="sticky top-0 z-40 w-full bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo/Brand */}
            <Link to="/" className="font-bold text-xl text-green-600">
              {/* FeedbackbyAI Or your actual brand name */}
            </Link>

            {/* Header Actions/Nav */}
            <div className="flex items-center gap-4">
              {/* Credits Badge - Now using the component */}
              <CreditsBadge credits={userCredits.video_credits} />

              {/* Links */}
              <Link to="/video-library" className="text-sm font-medium text-gray-500 hover:text-gray-800 flex items-center gap-1">
                <FileVideo className="w-4 h-4" />
                <span>My Videos</span>
              </Link>
              <Link to="/dashboard" className="text-sm font-medium text-gray-500 hover:text-gray-800">
                Dashboard
              </Link>
              {/* Maybe Add Account/Logout Dropdown here */}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-grow w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Page Title Section - Simplified */}
        <div className="relative text-center py-6 md:py-8 mb-6 overflow-hidden">
          {/* Subtle background accent */}
          <div className="absolute inset-0 bg-gradient-to-r from-gray-50 via-white to-gray-50 -z-10"></div>

          {/* Content container with improved spacing */}
          <div className="max-w-4xl mx-auto px-4">
            {/* Main heading with refined typography */}
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-gray-900 tracking-tight leading-tight mb-3 flex items-center justify-center">
              <span className="text-green-500 mr-2">🎥</span> Launch Your AI Influencer in Minutes
            </h1>

            {/* Refined subheading */}
            <p className="text-sm md:text-base text-gray-600 max-w-2xl mx-auto leading-relaxed mb-5">
              Create scroll-stopping TikToks, Reels & Shorts — no camera, no editing, just real results.
            </p>

            {/* Visual element to highlight key formula */}
            <div className="inline-flex items-center py-2 px-4 bg-gray-50 rounded-full text-sm text-gray-700 font-medium border border-gray-200 shadow-sm">
              <span>Hook Scene</span>
              <span className="mx-1 text-gray-400">+</span>
              <span>Demo</span>
              <span className="mx-1 text-gray-400">=</span>
              <span className="text-green-600">Auto-Generated Video That Sells</span>
            </div>
          </div>
        </div>

        {/* Render the main editor content */}
        {renderStepContent()}

        {/* Old Action Buttons (Next/Prev) are REMOVED */}
      </main>

      {/* Footer (Optional) */}
      <footer className="py-4 text-center text-xs text-gray-400 border-t border-gray-100 mt-12">
        © {new Date().getFullYear()} FeedbackbyAI. All rights reserved.
      </footer>

      {/* Modals (Processing, Custom Template, etc.) - Keep these outside main layout */}
      {processing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-white rounded-2xl w-full max-w-md mx-4 shadow-xl p-6">
            {/* Preview/Thumbnail */}
            <div className="aspect-[9/16] w-full rounded-xl overflow-hidden bg-gray-100 mb-6">
              <div className="w-full h-full flex flex-col items-center justify-center p-4 relative">
                <div className="absolute flex flex-col items-center">
                  <Loader2 className="w-12 h-12 animate-spin text-green-500 mb-3" />
                  <p className="text-center text-gray-700 font-medium">Processing your video...</p>
                  <p className="text-sm text-gray-500 mt-2">You'll be notified when it's ready</p>
                </div>
              </div>
            </div>

            {/* Tips */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
              <h4 className="font-medium text-blue-800 mb-2">While you wait</h4>
              <p className="text-sm text-blue-700">
                You can continue browsing or create another video while this one processes. We'll notify you when your video is ready!
              </p>

              {/* Action to minimize or continue working */}
              <div className="mt-3 flex justify-end">
                <button
                  onClick={() => {
                    // Keep processing in background but hide modal
                    document.getElementById("notification-container")?.appendChild(
                      Object.assign(document.createElement("div"), {
                        className: "fixed bottom-4 right-4 bg-green-50 p-4 rounded-lg shadow-lg border border-green-200",
                        innerHTML: `
                    <div class="flex items-center">
                      <div class="mr-3">
                        <Loader2 class="w-5 h-5 text-green-500 animate-spin" />
                      </div>
                      <div>
                        <p class="text-sm font-medium text-green-800">Processing in background</p>
                        <p class="text-xs text-green-600">We'll notify you when your video is ready</p>
                      </div>
                    </div>
                  `,
                      })
                    );

                    // Hide modal but keep processing state
                    const modalElement = document.querySelector(".fixed.inset-0.z-50");
                    if (modalElement) {
                      modalElement.style.display = "none";
                    }
                  }}
                  className="text-sm text-green-700 hover:text-green-800 font-medium"
                >
                  Continue Working
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Notification Container - Ensure this exists for showNotification */}
      <div id="notification-container" className="fixed top-4 right-4 z-50 flex flex-col gap-2"></div>
    </div> // End Root Div
  );
};

export default VideoCreator;
